/*
  Mattress product page (sections/mj-mattress-product.liquid), October 2026. No dependencies.

  Size and option buttons, price, sold-out state, the matching protector box, the gallery, the
  quantity buttons, the size guide and the sticky Add to Cart bar.

  Add to Cart itself is handled by assets/add-on-drawer-new.js (the same script as every other
  product page): it posts the mattress plus any ticked input[data-mj-addon-extra] to /cart/add.json,
  refreshes the basket and shows the "Added to Cart" pop-up. That script sets
  window.mjAddonExtrasSupported; without it the protector box stays hidden, so a shopper can never
  tick something that wouldn't be added.

  v18 (12 Oct 2026), with assets/mj-express.js: the express buttons carry the delivery date the basket would
  pick, and step aside while the protector or an add-on is ticked (they bought the mattress alone), with a hint
  that express comes next; sold-out sizes hide them too.
*/
(() => {
  'use strict';

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const norm = (value) => String(value == null ? '' : value).trim().toLowerCase();

  /* ---------- Money ---------- */
  function formatMoney(cents) {
    const format = String(window.money_format || '£{{amount}}').replace(/<[^>]*>/g, '');
    const value = Number(cents) || 0;
    const number = (decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return number(0);
        case 'amount_with_comma_separator': return number(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return number(0, '.', ',');
        case 'amount_with_space_separator': return number(2, ' ', ',');
        case 'amount_with_apostrophe_separator': return number(2, "'", '.');
        default: return number(2);
      }
    });
  }
  const formatMoneyShort = (cents) => formatMoney(cents).replace(/\.00(?!\d)/, '');

  /* ---------- Size guide (any [data-mjm-open-dialog]) ---------- */
  function openDialog(id, opener) {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') return false;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    document.documentElement.classList.add('mjm-no-scroll');
    return true;
  }

  if (!window.__mjmDialogs) {
    window.__mjmDialogs = true;
    document.addEventListener('click', (event) => {
      const target = event.target;
      // A click on the backdrop lands on the dialog itself
      if (target instanceof HTMLDialogElement && target.open && target.classList.contains('mjm-modal')) {
        const box = target.getBoundingClientRect();
        const inside = event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
        if (!inside) target.close();
        return;
      }
      const opener = target.closest('[data-mjm-open-dialog]');
      if (opener) {
        event.preventDefault();
        openDialog(opener.dataset.mjmOpenDialog, opener);
        return;
      }
      const closer = target.closest('[data-mjm-close-dialog]');
      if (closer) {
        const dialog = closer.closest('dialog');
        if (dialog) dialog.close();
      }
    });
    document.addEventListener('close', (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('mjm-modal')) return;
      if (!document.querySelector('dialog.mjm-modal[open]')) document.documentElement.classList.remove('mjm-no-scroll');
      if (dialog._opener && document.contains(dialog._opener)) dialog._opener.focus();
    }, true);
  }

  /* ---------- Tabs and questions: button[aria-controls] inside [data-mjm-acc] ---------- */
  if (!window.__mjmAccordions) {
    window.__mjmAccordions = true;
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-mjm-acc] > button[aria-controls]');
      if (!button || !button.closest('.mjm')) return;
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      button.parentElement.classList.toggle('is-open', open);
    });
  }

  /* ---------- Gallery ---------- */
  function goToMedia(section, mediaId, smooth = true) {
    const slides = $('[data-mjm-slides]', section);
    const slide = slides && $(`[data-media-id="${mediaId}"]`, slides);
    if (!slide) return;
    const left = slide.getBoundingClientRect().left - slides.getBoundingClientRect().left + slides.scrollLeft;
    slides.scrollTo({ left, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
    markThumb(section, String(mediaId));
  }

  function markThumb(section, mediaId) {
    $$('[data-mjm-media-target]', section).forEach((thumb) => {
      const active = thumb.dataset.mjmMediaTarget === mediaId;
      thumb.classList.toggle('is-active', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }

  function initGallery(section) {
    const slides = $('[data-mjm-slides]', section);
    if (!slides) return;
    const thumbs = $$('[data-mjm-media-target]', section);
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => goToMedia(section, thumb.dataset.mjmMediaTarget));
    });
    if (!('IntersectionObserver' in window) || thumbs.length === 0) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
        const id = entry.target.dataset.mediaId;
        markThumb(section, id);
        const thumb = thumbs.find((t) => t.dataset.mjmMediaTarget === id);
        const strip = thumb && thumb.parentElement;
        if (strip && strip.scrollWidth > strip.clientWidth) {
          const offset = thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2;
          strip.scrollTo({ left: Math.max(0, offset), behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
      });
    }, { root: slides, threshold: [0.6] });
    $$('[data-media-id]', slides).forEach((slide) => observer.observe(slide));
  }

  /* ---------- Product ---------- */
  function initSection(section) {
    if (!section || section._mjmInit) return;
    section._mjmInit = true;

    let data;
    try {
      data = JSON.parse($('[data-mjm-product-json]', section).textContent);
    } catch (error) {
      return;
    }
    const variants = data.variants || [];
    const buttonLabel = data.buttonLabel || 'Add to basket';
    const fieldsets = $$('[data-mjm-option]', section);
    const sizeIndex = fieldsets.findIndex((fieldset) => fieldset.classList.contains('mjm-option--size'));
    const form = $('form.mjm-form', section);
    const idInput = form && $('[data-mjm-variant-id]', form);
    const addButton = form && $('[data-mjm-add]', form);
    const addLabel = addButton && $('[data-mjm-add-label]', addButton);
    const price = $('[data-mjm-price]', section);
    const sticky = $('[data-mjm-sticky]', section);
    const stickyButton = sticky && $('[data-mjm-sticky-add]', sticky);
    const addon = $('[data-mjm-addon]', section);
    const addonInput = addon && $('[data-mj-addon-extra]', addon);
    let addonData = null;
    try {
      const json = addon && $('[data-mjm-addon-json]', addon);
      addonData = json ? JSON.parse(json.textContent) : null;
    } catch (error) {
      addonData = null;
    }
    // Only offer the protector when the Add to Cart script knows how to add it.
    const addonSupported = window.mjAddonExtrasSupported === true;
    if (addon && !addonSupported) {
      addon.hidden = true;
      if (addonInput) addonInput.checked = false;
    }

    // v18: the express buttons' delivery date (assets/mj-express.js). Shopify's product button buys the
    // mattress alone, so it steps aside while the protector or an add-on is ticked, as on the bed page.
    const dynamic = form && $('.mjm-dynamic', form);
    const expressHint = form && $('[data-mj-express-hint]', form);
    let expressVariant = null;
    const express = window.MJExpress && form ? window.MJExpress.product({
      form,
      rules: window.MJExpress.rulesIn(section),
      note: $('[data-mj-express-note]', form),
      express: dynamic,
      variant: () => expressVariant,
    }) : null;
    // The upsell groups (assets/add-on-drawer-new.js) hide Shopify's button while one of theirs is ticked
    const paymentButton = dynamic && $('[data-shopify="payment-button"]', dynamic);
    const drawerTicked = () => !!(paymentButton && paymentButton.style.display === 'none');
    function updateExpress() {
      if (!dynamic) return;
      const available = !!(expressVariant && expressVariant.available);
      const extras = !!(addonInput && addonInput.checked && addon && !addon.hidden) || drawerTicked();
      dynamic.hidden = extras || !available;
      if (expressHint) expressHint.hidden = !(extras && available);
      if (express) express.update();
    }
    if (paymentButton && 'MutationObserver' in window) {
      new MutationObserver(updateExpress).observe(paymentButton, { attributes: true, attributeFilter: ['style'] });
    }

    const selectedOptions = () => fieldsets.map((fieldset) => {
      const checked = $('input:checked', fieldset);
      return checked ? checked.value : null;
    });
    const findVariant = (options) => variants.find((variant) => variant.options.every((value, i) => value === options[i]));

    function markAvailability(options) {
      fieldsets.forEach((fieldset, index) => {
        $$('input', fieldset).forEach((input) => {
          const test = options.slice();
          test[index] = input.value;
          const match = findVariant(test);
          const label = $(`label[for="${input.id}"]`, fieldset);
          if (!label) return;
          const unavailable = !match || !match.available;
          label.classList.toggle('is-unavailable', unavailable);
          const status = $('[data-mjm-pill-status]', label);
          if (status) status.textContent = !match ? ' (unavailable)' : (match.available ? '' : ' (sold out)');
        });
      });
    }

    function setButtons(enabled, text) {
      if (addButton) addButton.disabled = !enabled;
      if (addLabel) addLabel.textContent = text;
      if (stickyButton) {
        stickyButton.disabled = !enabled;
        stickyButton.textContent = text;
      }
    }

    function renderPrice(variant) {
      if (!price) return;
      const current = $('[data-mjm-price-current]', price);
      const compare = $('[data-mjm-price-compare]', price);
      const compareValue = $('[data-mjm-price-compare-value]', price);
      const save = $('[data-mjm-price-save]', price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (current) current.textContent = formatMoney(variant.price);
      price.classList.toggle('is-sale', Boolean(onSale));
      if (compare) compare.hidden = !onSale;
      if (compareValue) compareValue.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
      if (save) {
        save.hidden = !onSale;
        save.textContent = onSale ? `Save ${formatMoneyShort(variant.compare_at_price - variant.price)}` : '';
      }
    }

    function updateAddon(variant) {
      if (!addon || !addonData || !addonInput || !addonSupported) return;
      const key = variant && variant.available ? norm(sizeIndex >= 0 ? variant.options[sizeIndex] : variant.title) : '';
      const match = key && addonData.variants.find((v) => norm(v.title) === key && v.available);
      if (!match) {
        addon.hidden = true;
        addonInput.checked = false;
        addonInput.dataset.variantId = '';
        addonInput.dataset.price = '';
        addonInput.dataset.size = '';
        return;
      }
      addon.hidden = false;
      addonInput.dataset.variantId = String(match.id);
      addonInput.dataset.price = String(match.price);
      addonInput.dataset.size = match.title;
      const size = $('[data-mjm-addon-size]', addon);
      const cost = $('[data-mjm-addon-price]', addon);
      if (size) size.textContent = match.title;
      if (cost) cost.textContent = `+${formatMoney(match.price)}`;
    }

    function update(event) {
      const options = selectedOptions();
      fieldsets.forEach((fieldset, i) => {
        const label = $('[data-mjm-option-selected]', fieldset);
        if (label) label.textContent = options[i] || '';
      });
      markAvailability(options);
      const variant = findVariant(options);
      expressVariant = variant || null;
      const eta = $('[data-mjm-eta]', section);
      if (eta) eta.classList.toggle('is-no-eta', !variant || !variant.available);
      if (!variant) {
        setButtons(false, 'Unavailable');
        updateAddon(null);
        updateExpress();
        return;
      }
      if (idInput && idInput.value !== String(variant.id)) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      setButtons(variant.available, variant.available ? buttonLabel : 'Sold out');
      renderPrice(variant);
      updateAddon(variant);
      if (variant.featured_media) goToMedia(section, variant.featured_media);
      if (sticky) {
        const stickyVariant = $('[data-mjm-sticky-variant]', sticky);
        const stickyPrice = $('[data-mjm-sticky-price]', sticky);
        if (stickyVariant) stickyVariant.textContent = variant.title;
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      }
      // Rewrite the address only when the shopper changed the size: every URL change counts as a
      // page view in GA4 (history-change tracking).
      if (event && event.isTrusted && data.url) {
        const next = `${data.url}?variant=${variant.id}`;
        if (window.location.pathname + window.location.search !== next) window.history.replaceState({}, '', next);
      }
      document.dispatchEvent(new CustomEvent('mjm:variant-change', { detail: { variant } }));
      updateExpress();
    }

    section.addEventListener('change', (event) => {
      if (event.target.matches('.mjm-option__input')) update(event);
    });

    // A ?variant= in the address, or a browser that kept an old choice on back/forward
    update(null);

    /* Quantity */
    $$('[data-mjm-qty-change]', section).forEach((button) => {
      button.addEventListener('click', () => {
        const input = $('input', button.closest('[data-mjm-qty]'));
        if (!input) return;
        const min = input.min !== '' ? Number(input.min) : 1;
        const max = input.max !== '' ? Number(input.max) : Infinity;
        const next = Math.min(max, Math.max(min, (Number(input.value) || min) + Number(button.dataset.mjmQtyChange)));
        if (next === Number(input.value)) return;
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    const qtyInput = $('[data-mjm-qty] input', section);
    if (qtyInput) {
      qtyInput.addEventListener('change', () => {
        const min = Number(qtyInput.min) || 1;
        const max = qtyInput.max !== '' ? Number(qtyInput.max) : Infinity;
        const value = Math.round(Number(qtyInput.value));
        qtyInput.value = Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;
      });
    }

    /* Protector box: let the sticky total and any listeners know */
    if (addonInput) {
      addonInput.addEventListener('change', () => {
        updateExpress();
        document.dispatchEvent(new CustomEvent('mjm:addon-change', { detail: { checked: addonInput.checked } }));
        // Shopify customer events (Settings > Customer events); the MJ custom pixel forwards them to GA4
        if (addonInput.checked) {
          try {
            if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
              window.Shopify.analytics.publish('mj_addon_tick', {
                page: 'mattress',
                kind: 'protector',
                group: 'Protector',
                item_id: addonInput.dataset.variantId || '',
                item_name: 'Mattress protector',
                price: (Number(addonInput.dataset.price) || 0) / 100,
                product: data.title || '',
                variant: addonInput.dataset.size || '',
              });
            }
          } catch (error) { /* tracking must never break the page */ }
        }
      });
    }

    /* Sticky bar: shows once the main button has scrolled out of view, hides over the footer */
    if (sticky && addButton && 'IntersectionObserver' in window) {
      const footer = document.querySelector('[id$="__footer"], .shopify-section-group-footer-group');
      let pastButton = false;
      let overFooter = false;
      const setShown = () => {
        const show = pastButton && !overFooter;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        document.body.classList.toggle('mj-sticky-on', show);
        if (stickyButton) stickyButton.tabIndex = show ? 0 : -1;
      };
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === addButton) pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          else overFooter = entry.isIntersecting;
        });
        setShown();
      });
      observer.observe(addButton);
      if (footer) observer.observe(footer);
      if (stickyButton) {
        stickyButton.addEventListener('click', () => {
          if (addButton.disabled) return;
          addButton.click();
        });
      }
    }

    initGallery(section);
  }

  function init(root = document) {
    $$('[data-mjm-product]', root).forEach(initSection);
  }

  // Wait for DOMContentLoaded even though this script is deferred: add-on-drawer-new.js is deferred
  // too, comes later in the page, and has to have run before the protector box is switched on.
  if (document.readyState === 'complete') init();
  else {
    document.addEventListener('DOMContentLoaded', () => init());
    window.addEventListener('load', () => init());
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
